import { useState, useEffect, useRef } from 'react';
import { useModalStore } from '../../../stores/modalStore.js';
import { useServerStore } from '../../../stores/serverStore.js';
import { useFocusTrap } from '../../../hooks/useFocusTrap.js';

const AUTH = () => ({ Authorization: `Bearer ${localStorage.getItem('auth_token')}` });

const DEMO_SERVERS = [
  { id: 'disc-aethex',    name: 'AeThex Community',  icon: '⚡', memberCount: 4821, description: 'Official AeThex community — support, announcements, and general chat.',        tags: ['community', 'official', 'support'] },
  { id: 'disc-dev',       name: 'Dev Lounge',         icon: '💻', memberCount: 3104, description: 'A chill space for developers to share projects, get help, and collaborate.',   tags: ['dev', 'coding', 'tech'] },
  { id: 'disc-gaming',    name: 'GG Hub',             icon: '🎮', memberCount: 9230, description: 'Gaming community for all platforms. LFG, tournaments, and game discussion.',   tags: ['gaming', 'community'] },
  { id: 'disc-music',     name: 'Frequency',          icon: '🎵', memberCount: 2670, description: 'Music producers, listeners, and critics sharing what they love.',              tags: ['music', 'art', 'chill'] },
  { id: 'disc-anime',     name: 'Sakura Lounge',      icon: '🌸', memberCount: 6410, description: 'Anime, manga, and Japanese culture discussion. All genres welcome.',           tags: ['anime', 'community'] },
  { id: 'disc-study',     name: 'Study Hall',         icon: '📚', memberCount: 1890, description: 'Focused study groups, notes sharing, and accountability partners.',           tags: ['study', 'education', 'productivity'] },
];

const TAG_COLORS = {
  community: '#6366f1', official: '#f59e0b', support: '#0ea5e9',
  dev: '#10b981', coding: '#10b981', tech: '#0ea5e9',
  gaming: '#ec4899', chill: '#8b5cf6', voice: '#6366f1',
  music: '#f97316', art: '#ec4899', creative: '#8b5cf6',
  study: '#14b8a6', education: '#0ea5e9', productivity: '#10b981',
  anime: '#f43f5e',
};

function TagBadge({ tag }) {
  const color = TAG_COLORS[tag] || '#6b7280';
  return (
    <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 10, background: `${color}22`, color, border: `1px solid ${color}44`, letterSpacing: '0.03em' }}>
      {tag}
    </span>
  );
}

function ServerCard({ server, onJoin, joining }) {
  return (
    <div style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10, padding: '16px', display: 'flex', flexDirection: 'column', gap: 10, transition: 'border-color 0.2s' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{ width: 48, height: 48, borderRadius: 12, background: 'linear-gradient(135deg,#1e1b4b,#312e81)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24, flexShrink: 0 }}>
          {server.icon || '🌐'}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{server.name}</div>
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', marginTop: 2 }}>
            <span style={{ color: '#3ba55c', fontWeight: 600 }}>●</span> {server.memberCount?.toLocaleString() || '0'} members
          </div>
        </div>
      </div>

      <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.55)', lineHeight: 1.5, flex: 1 }}>
        {server.description || 'No description.'}
      </div>

      {server.tags?.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
          {server.tags.map(t => <TagBadge key={t} tag={t} />)}
        </div>
      )}

      <button
        onClick={() => onJoin(server)}
        disabled={joining === server.id}
        style={{
          padding: '7px', borderRadius: 7, border: 'none', fontWeight: 600, fontSize: 13, cursor: joining === server.id ? 'default' : 'pointer',
          background: joining === server.id ? 'rgba(99,102,241,0.35)' : '#6366f1', color: '#fff',
        }}
      >
        {joining === server.id ? 'Joining…' : 'Join Server'}
      </button>
    </div>
  );
}

const POPULAR_TAGS = ['community', 'gaming', 'dev', 'music', 'anime', 'study', 'art', 'chill'];

export function ServerDiscoveryModal() {
  const { isOpen, type, onClose } = useModalStore();
  const addServer = useServerStore(s => s.addServer);

  const [servers, setServers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState('');
  const [activeTag, setActiveTag] = useState('');
  const [sort, setSort] = useState('members');
  const [joining, setJoining] = useState(null);
  const [joinMsg, setJoinMsg] = useState(null);

  const ref = useRef();
  useFocusTrap(ref, isOpen && type === 'serverDiscovery');

  const load = async (q = query, tag = activeTag, s = sort) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ q, tag, sort: s });
      const r = await fetch(`/api/discover?${params}`, { headers: AUTH() });
      const d = await r.json();
      const list = d.servers || [];
      setServers(list.length > 0 ? list : DEMO_SERVERS);
    } catch {
      setServers(DEMO_SERVERS);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && type === 'serverDiscovery') {
      setQuery('');
      setActiveTag('');
      setSort('members');
      load('', '', 'members');
    }
  }, [isOpen, type]);

  useEffect(() => {
    if (!isOpen || type !== 'serverDiscovery') return;
    const t = setTimeout(() => load(query, activeTag, sort), 300);
    return () => clearTimeout(t);
  }, [query, activeTag, sort]);

  if (!isOpen || type !== 'serverDiscovery') return null;

  const handleJoin = async (server) => {
    setJoining(server.id);
    try {
      const r = await fetch(`/api/discover/${server.id}/join`, { method: 'POST', headers: AUTH() });
      const d = await r.json();
      if (d.success || server.id.startsWith('disc-')) {
        setJoinMsg(`Joined ${server.name}!`);
        addServer?.({ id: server.id, name: server.name, icon: server.icon || '🌐' });
        setTimeout(() => setJoinMsg(null), 3000);
        // Auto-trigger onboarding if first time joining this server
        const key = `onboarded-${server.id}`;
        if (!localStorage.getItem(key)) {
          localStorage.setItem(key, '1');
          setTimeout(() => onOpen('onboarding', { server, serverId: server.id }), 800);
        }
      }
    } catch {
      // For demo servers, always succeed
      if (server.id.startsWith('disc-')) {
        setJoinMsg(`Joined ${server.name}!`);
        addServer?.({ id: server.id, name: server.name, icon: server.icon || '🌐' });
        const key = `onboarded-${server.id}`;
        if (!localStorage.getItem(key)) {
          localStorage.setItem(key, '1');
          setTimeout(() => onOpen('onboarding', { server, serverId: server.id }), 800);
        }
        setTimeout(() => setJoinMsg(null), 3000);
      }
    }
    setJoining(null);
  };

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 1200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={onClose}
    >
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-label="Discover Servers"
        onClick={e => e.stopPropagation()}
        style={{ background: '#16213e', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 14, width: 760, maxWidth: '95vw', maxHeight: '85vh', display: 'flex', flexDirection: 'column', boxShadow: '0 32px 80px rgba(0,0,0,0.7)', overflow: 'hidden' }}
      >
        {/* Header */}
        <div style={{ padding: '20px 22px 0', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
            <div>
              <div style={{ fontSize: 20, fontWeight: 800, color: '#fff' }}>🔭 Discover Servers</div>
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', marginTop: 3 }}>Find public communities to join</div>
            </div>
            <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.4)', fontSize: 20, cursor: 'pointer', lineHeight: 1 }}>✕</button>
          </div>

          {/* Search + sort */}
          <div style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
            <div style={{ position: 'relative', flex: 1 }}>
              <span style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', color: 'rgba(255,255,255,0.3)', fontSize: 14 }}>🔍</span>
              <input
                value={query}
                onChange={e => setQuery(e.target.value)}
                placeholder="Search servers…"
                style={{ width: '100%', padding: '9px 12px 9px 33px', borderRadius: 8, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', color: '#fff', fontSize: 14, outline: 'none', boxSizing: 'border-box' }}
              />
            </div>
            <select
              value={sort}
              onChange={e => setSort(e.target.value)}
              style={{ padding: '9px 12px', borderRadius: 8, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.7)', fontSize: 13, cursor: 'pointer', outline: 'none' }}
            >
              <option value="members">Most Members</option>
              <option value="new">Newest</option>
            </select>
          </div>

          {/* Tag filters */}
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14, paddingBottom: 14, borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
            <button
              onClick={() => setActiveTag('')}
              style={{ padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: 'pointer', background: !activeTag ? '#6366f1' : 'rgba(255,255,255,0.06)', border: `1px solid ${!activeTag ? '#6366f1' : 'rgba(255,255,255,0.1)'}`, color: !activeTag ? '#fff' : 'rgba(255,255,255,0.5)' }}
            >
              All
            </button>
            {POPULAR_TAGS.map(tag => (
              <button
                key={tag}
                onClick={() => setActiveTag(activeTag === tag ? '' : tag)}
                style={{ padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: 'pointer', background: activeTag === tag ? `${TAG_COLORS[tag] || '#6366f1'}33` : 'rgba(255,255,255,0.05)', border: `1px solid ${activeTag === tag ? (TAG_COLORS[tag] || '#6366f1') : 'rgba(255,255,255,0.09)'}`, color: activeTag === tag ? (TAG_COLORS[tag] || '#a5b4fc') : 'rgba(255,255,255,0.5)' }}
              >
                {tag}
              </button>
            ))}
          </div>
        </div>

        {/* Grid */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '0 22px 22px' }}>
          {joinMsg && (
            <div style={{ padding: '10px 14px', borderRadius: 8, background: 'rgba(52,211,153,0.12)', border: '1px solid rgba(52,211,153,0.3)', color: '#34d399', fontSize: 13, marginBottom: 14 }}>
              ✓ {joinMsg}
            </div>
          )}

          {loading ? (
            <div style={{ textAlign: 'center', paddingTop: 50, color: 'rgba(255,255,255,0.3)' }}>Loading servers…</div>
          ) : servers.length === 0 ? (
            <div style={{ textAlign: 'center', paddingTop: 50, color: 'rgba(255,255,255,0.3)', fontSize: 14 }}>No servers found.</div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12 }}>
              {servers.map(s => (
                <ServerCard key={s.id} server={s} onJoin={handleJoin} joining={joining} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
